import {cloneDeep} from 'lodash';
import React, {useEffect, useState, useRef} from 'react';
import {useLocation, useLoaderData, useNavigation, useNavigate} from "react-router-dom";
import {isMobile} from "react-device-detect";
import {getAuth, signOut} from "firebase/auth";

import {isAdmin} from 'ultra/helpers/auth';
import {DEFAULT_CITY} from 'ultra/const/general';
import {isEmptyObj} from 'ultra/helpers/utils';
import {getClientCity, getCityConfig} from 'ultra/configs/general';
import {getShopDetails} from 'ultra/helpers/shop';
import {normalizeRoute} from 'ultra/helpers/route';

import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

// stores
import {useConfigStore} from '../../Stores/config'
import {useContentStore} from '../../Stores/content'
import {useToasterStore} from '../../Stores/toster';
import {useBackdropPreloaderStore} from '../../Stores/backdropPreloader';

// containers
import MainTemplate from './Templates/MainTemplate'

import SectionTemplate from './Templates/Containers/Section/base/SectionTemplate'

import NodeLinesTemplate from './Templates/Nodes/System/Node/view/NodeLinesTemplate'
import NodeLinesCompactTemplate from './Templates/Nodes/System/Node/view/NodeLinesCompactTemplate'
import NodeTileTemplate from './Templates/Nodes/System/Node/view/NodeTileTemplate'
import NodeTileSectionsTemplate from './Templates/Nodes/System/Node/view/NodeTileSectionsTemplate'
import NodeTableTemplate from './Templates/Nodes/System/Node/view/NodeTableTemplate'
import AfficheTileTemplate from './Templates/Nodes/System/Node/view/AfficheTileTemplate'

import LibraryTemplate from './Templates/Nodes/System/Node/custom/LibraryTemplate'
import LibraryListTemplate from './Templates/Nodes/System/Node/custom/LibraryListTemplate'
import ChatsTemplate from './Templates/Nodes/System/Node/custom/ChatsTemplate'
import ShopsTileTemplate from './Templates/Nodes/System/Node/custom/ShopsTileTemplate'
import ScamersTemplate from './Templates/Nodes/System/Node/custom/ScamersTemplate'

import LinksTemplate from './Templates/Containers/Links/base/LinksTemplate'
import SectionTileTemplate from './Templates/Containers/Section/view/SectionTileTemplate'

import SectionGalleryTemplate from './Templates/Containers/Section/view/SectionGalleryTemplate'
import SalesTemplate from './Templates/Containers/Section/custom/SalesTemplate'
import ServicesTemplate from './Templates/Containers/Section/custom/ServicesTemplate'

// nodes
import NodeTemplate from './Templates/Nodes/System/Node/base/NodeTemplate'

import AliasTemplate from './Templates/Nodes/System/Alias/base/AliasTemplate'
import FormTemplate from './Templates/Nodes/System/Form/base/FormTemplate'

import ScamTemplate from './Templates/Nodes/Custom/Scam/base/ScamTemplate'
import SaleTemplate from './Templates/Nodes/Custom/Sale/base/SaleTemplate'
import RecommendationTemplate from './Templates/Nodes/Custom/Recommendation/base/RecommendationTemplate'
import LocationTemplate from './Templates/Nodes/Custom/Location/base/LocationTemplate'
import ActivityTemplate from './Templates/Nodes/Custom/Service/base/ServiceTemplate'
import CitiesTemplate from './Templates/Nodes/Custom/Cities/base/CitiesTemplate'

import NetworkingTemplate from './Templates/Nodes/Custom/Networking/base/NetworkingTemplate'

import AnalogueTemplate from './Templates/Nodes/Custom/Analogue/base/AnalogueTemplate'
import DoctorTemplate from './Templates/Nodes/Custom/Doctor/base/DoctorTemplate'
import ClinicTemplate from './Templates/Nodes/Custom/Clinic/base/ClinicTemplate'

import ChatTemplate from './Templates/Nodes/Custom/Chat/base/ChatTemplate'
import UrlTemplate from './Templates/Nodes/Custom/Url/base/UrlTemplate'

import BookTemplate from './Templates/Nodes/Custom/Book/base/BookTemplate'

// complex
import WikiTemplate from './Templates/Complex/Wiki/Wiki/base/WikiTemplate'
import WikiNoteTemplate from './Templates/Complex/Wiki/Note/base/WikiNoteTemplate'

import NewsTemplate from './Templates/Complex/News/News/base/NewsTemplate'
import NewsListTemplate from './Templates/Complex/News/NewsList/base/NewsListTemplate'

import EventsTemplate from './Templates/Complex/Events/Events/base/EventsTemplate'
import EventTemplate from './Templates/Complex/Events/Event/base/EventTemplate'
import TourTemplate from './Templates/Complex/Events/Tour/base/TourTemplate'
import RetreatTemplate from './Templates/Complex/Events/Retreat/base/RetreatTemplate'


// modules
import ShopTemplate from './Templates/Modules/Shop/Shop/base/ShopTemplate'

import ShopSectionTemplate from './Templates/Modules/Shop/Section/base/ShopSectionTemplate'
import ShopSectionTableTemplate from './Templates/Modules/Shop/Section/custom/ShopSectionTableTemplate'

import ShopProductTemplate from './Templates/Modules/Shop/Product/base/ShopProductTemplate'
import JeweleryProductTemplate from './Templates/Modules/Shop/Product/custom/JeweleryProductTemplate'
import MotoTemplate from './Templates/Modules/Shop/Product/custom/MotoTemplate'

import InsuranceTemplate from './Templates/Modules/Insurance/base/InsuranceTemplate'

// widgets
import UpdatePassword from '../Profile/Components/UpdatePassword';

import NodeActions from '../Content/Widgets/NodeActions'

import {useGlobalCustomContext} from '../../Helpers/context';
import {getNodeContent} from '../../Helpers/content';
import {setBodyClass, setPageTitle} from '../../Helpers/router';
import {getTelegramMode} from '../../Helpers/telegram';

import {NODE_ACTIONS} from './Reducers/node';

import {USER_ACTIONS} from '../../Modules/Profile/Reducers/user';
import {userLogout} from '../../Modules/Profile/Helpers/user';

import AdaptiveDialog from '../../Components/Adaptive/Dialog';

import GlobalSearch from '../../Components/GlobalSearch'

import './index.scss';

const Templates = {
    // containers
    main: MainTemplate,

    section: SectionTemplate,
    links: LinksTemplate,

    // view:
    tile: NodeTileTemplate,
    lines: NodeLinesTemplate,
    linesCompact: NodeLinesCompactTemplate,
    tileSections: NodeTileSectionsTemplate,
    table: NodeTableTemplate,
    affiche: AfficheTileTemplate,

    library: LibraryTemplate,
    libraryList: LibraryListTemplate,
    chats: ChatsTemplate,
    scamers: ScamersTemplate,

    sectionTile: SectionTileTemplate,
    galleries: SectionGalleryTemplate,

    sales: SalesTemplate,
    services: ServicesTemplate,

    // nodes
    node: NodeTemplate,
    alias: AliasTemplate,
    form: FormTemplate,

    newsList: NewsListTemplate,
    news: NewsTemplate,

    networking: NetworkingTemplate,
    sale: SaleTemplate,
    location: LocationTemplate,
    recommendation: RecommendationTemplate,

    service: ActivityTemplate,

    cities: CitiesTemplate,
    shopsTile: ShopsTileTemplate,

    analogue: AnalogueTemplate,
    doctor: DoctorTemplate,
    clinic: ClinicTemplate,

    url: UrlTemplate,
    chat: ChatTemplate,

    scam: ScamTemplate,

    // modules
    insurance: InsuranceTemplate,

    shop: ShopTemplate,
    shopSection: ShopSectionTemplate,
    shopProduct: ShopProductTemplate,

    shopSectionTable: ShopSectionTableTemplate,

    jewelery: JeweleryProductTemplate,

    moto: MotoTemplate,

    // complex
    wiki: WikiTemplate,
    wikiNote: WikiNoteTemplate,

    topic: NodeTemplate,
    conversation: NodeTemplate,

    events: EventsTemplate,
    event: EventTemplate,
    tour: TourTemplate,
    retreat: RetreatTemplate,

    book: BookTemplate,
};


export default function Content() {
    // TODO: do not use useLoaderData one more time ! no reload page
    const loaderPageConfig = useLoaderData();

    const navigation = useNavigation();

    const {nodeState, userState, nodeDispatch, userDispatch} = useGlobalCustomContext();
    const {showBackdropPreloader, hideBackdropPreloader} = useBackdropPreloaderStore()
    const {showSuccess, showError} = useToasterStore();
    const {configs} = useConfigStore();

    const [editPassPopup, setEditPassPopup] = useState(false);
    const [pageConfig, setPageConfig] = useState(loaderPageConfig);
    const [Node, setNode] = useState(null);
    const [maintainanceMode, setMaintainanceMode] = useState(configs?.layout?.maintainanceMode || false);
    // const [isApplyActionCodeInProgress, setApplyActionCodeInProgress] = useState();

    const location = useLocation();
    const lastHash = useRef('');
    const navigate = useNavigate();

    const city = getClientCity(window);

    const {setContent} = useContentStore();

    const auth = getAuth();

    useEffect(() => {
        if (location.hash) {
            // safe hash for further use after navigation
            lastHash.current = location.hash.slice(1);
          }

          if (lastHash.current && document.getElementById(lastHash.current)) {
            setTimeout(() => {
              document
                .getElementById(lastHash.current)
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

              lastHash.current = '';
            }, 100);
          }
    })

    useEffect(() => {
        let bot = getTelegramMode();
        if (bot) {
            const handleGlobalClick = (event) => {
                const link = event.target.closest('a');
                const href = link.getAttribute('href');

                if (link.hostname === window.location.hostname) {
                    event.preventDefault();
                    console.log("href", href)
                    console.log("link", link)
                    navigate(link.pathname + link.search);
                }
            };

            document.addEventListener('click', handleGlobalClick);

            return () => {
                document.removeEventListener('click', handleGlobalClick);
            };
        }
    }, []);

    // loading value for skeletons
    useEffect(() => {
        setPageConfig(loaderPageConfig);

        if (navigation.state === 'loading') {
            if (nodeState.globalSearchVisible) nodeDispatch({type: NODE_ACTIONS.GLOBAL_SEARCH_TOGGLE, data: false});
        }

        if (navigation.state === 'idle') {
            const url = new URL(window.location.href);

            const mode = url.searchParams.get('mode');
            if (mode === 'verifyEmail' || mode === 'resetPassword') {
                if (mode === 'verifyEmail') {
                    showBackdropPreloader();

                    // logout
                    userLogout()
                        .promise
                        .then(async () => {
                            signOut(auth);
                            // after login, code will be checked
                            userDispatch({type: USER_ACTIONS.LOGIN});
                        })
                }
                else if (mode === 'resetPassword') {
                    setEditPassPopup(true)
                }
            }

            const action = url.searchParams.get('action');
            if (action?.indexOf("add-node") === 0) {
                const action_parts = action.split(":")
                const data = {node: action_parts[1]}
                nodeDispatch({type: NODE_ACTIONS.POPUP_ADD_NODE, data});
            }
        }
        else if (navigation.state === 'loading') {
            // this code added for mobile
            // on dom changes, search field move down without this code
            const ContentDOM = window.document.getElementById('Content');
            ContentDOM.style.minHeight = ''
        }
    }, [navigation])

    useEffect(() => {
        updateDocumentTitle();
        updateBodyStyles();

        setContent(cloneDeep(pageConfig));

        let template = pageConfig.page.config.template || configs.content.nodes[pageConfig.page.content._type].template;
        if (isMobile) {
            const templateMobile = (pageConfig.page.config.templateMobile || configs.content.nodes[pageConfig.page.content._type]?.templateMobile);
            if (templateMobile) template = templateMobile;
        }

        if (pageConfig.page.content._type === 'shop-product') {
            const {shop} = getShopDetails(pageConfig.page.content, pageConfig.breadcrumbs);
            if (!shop) return;

            if (shop?.partner_config) template = shop.partner_config.productTemplate || template
        } else if (pageConfig.page.content._type === 'shop-section') {
            const {shop} = getShopDetails(pageConfig.page.content, pageConfig.breadcrumbs);
            if (!shop) return;

            if (shop?.partner_config) template = shop.partner_config.productSectionTemplate || template        
        }

        if (typeof Templates[template] !== 'undefined') {
            setNode(() => React.createElement(
                Templates[template],
                {
                    children: {...pageConfig.children},
                    widgets: {...pageConfig.widgets},
                    content: {...pageConfig.page.content, ...pageConfig?.page?.config?.values?.[pageConfig.page.content._type]},
                    config: {...pageConfig?.page?.config},
                    meta: {...pageConfig?.page?.meta},
                    form: {...pageConfig.form},
                    breadcrumbs: {...pageConfig.breadcrumbs},
                    permits: {...pageConfig.permits},
                    key: pageConfig.page.content._did,
                }
            ));
        } else {
            // component doesn't exist yet
            setNode(React.createElement(
                () => <div>{template} template has not been created yet</div>
            ));
        }
    }, [pageConfig])

    useEffect(() => {
        if (nodeState.updatePageContentStart) {
            nodeDispatch({type: NODE_ACTIONS.UPDATE_PAGE_CONTENT_END});

            const notAuthorizedUserUpdates = {...nodeState.updatePageContentStartNotAuthorized}

            if (!isEmptyObj(notAuthorizedUserUpdates)) {
                if (!pageConfig.children.order.includes(notAuthorizedUserUpdates._uri)) {
                    pageConfig.children.order.push(notAuthorizedUserUpdates._uri)
                }
                pageConfig.children.list[notAuthorizedUserUpdates._uri] = notAuthorizedUserUpdates.page
                setPageConfig({...pageConfig});
            }
            else {
                const path = {
                    city: city || DEFAULT_CITY,
                    uri: normalizeRoute(location.pathname)
                };

                getNodeContent(path, location.search)
                    .promise
                    .then(result => {
                        const data = result

                        if (!isEmptyObj(notAuthorizedUserUpdates)) {
                            if (!data.children.order.includes(notAuthorizedUserUpdates._uri)) {
                                data.children.order.push(notAuthorizedUserUpdates._uri)
                            }
                            data.children.list[notAuthorizedUserUpdates._uri] = notAuthorizedUserUpdates.page
                        }

                        setPageConfig(data)
                    })
            }
        }
    }, [nodeState])

    useEffect(() => {
        if (configs?.layout?.maintainanceMode) setMaintainanceMode(!isAdmin(userState?.user))
    }, [userState?.user, configs])

    // updage titles
    const updateDocumentTitle = () => {
        let title = ''

        const cityConfig = getCityConfig(configs, city);
        if (cityConfig) {
            title = cityConfig.city + ' | '
        }

        setPageTitle(pageConfig.page.content.title);

        const classes = []

        classes.push(`template_${pageConfig.page.config.template || configs.content.nodes[pageConfig.page.content._type].template}`)
        classes.push(`node_${pageConfig?.page?.content?._type}`)

        if (pageConfig.page.content._uri && pageConfig.page.content._uri.length > 1)
            classes.push(`uri${pageConfig.page.content._uri.replaceAll('\\', '_')}`);

        if (pageConfig.page.content._did)
            classes.push(`did_${pageConfig.page.content._did}`);

        setBodyClass(classes)
    }

    const updateBodyStyles = () => {
        // if (isMobile) document.body.classList.add('mobile');
        const url = new URL(window.location.href);
        const viewList = url.searchParams.get('view') === 'list';
        if (viewList) document.body.classList.add('view_list');
    }

    const hideEditPassPopup = () => {
        navigate(window.location.pathname);
        setEditPassPopup(false)
    }

    return <div id='Content' className='Content'>
        {!pageConfig.page.content._published && <div className='globalInformer error'>
            <ErrorOutlineOutlinedIcon /> <span>Сторінка знаходиться на премодерації. Її бачите лише автор та модератори</span>
        </div>}

        {maintainanceMode && <div className='NodeContent'>
            <div className='maintainanceMode'>
                <div className='icon'>⚠️</div>
                <p className='title'>Додаток тимчасово недоступний у зв'язку з оновленням системи</p>
                <ul className='contacts'>
                    <li>Обмін валют: <a href="https://t.me/COCONUT_EXCHANGE">@COCONUT_EXCHANGE</a></li>
                    <li>Оформити візу: <a href="https://t.me/travelwithpanda">@travelwithpanda</a></li>
                    <li>Підтримка: <a href="https://t.me/miy_prostir">@miy_prostir</a></li>
                </ul>
            </div>
        </div>}

        {!maintainanceMode && <>
            {!isMobile && <GlobalSearch collapsable={true} />}
            <div className='NodeContent'>{Node}</div>
        </>}

        <span id='page_did'>{pageConfig.page.content._did}</span>

        <NodeActions
            config={pageConfig}
            content={pageConfig.page.content}
        />

        <AdaptiveDialog open={editPassPopup} onClose={hideEditPassPopup} title="Зміна паролю">
            <UpdatePassword onClose={hideEditPassPopup} />
        </AdaptiveDialog>
    </div>
}
